import { useEffect, useMemo, useState } from 'react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/lib/trpc/router';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@predoc/cow';
import { useGetFacesheetReferenceDetail } from '@/feature/patients/hooks/facesheetAPI';
import { useGetPatientById } from '@/feature/patients/hooks/patientsAPI';
import { FacesheetLoadError } from './FacesheetLoadError';
import { FacesheetSummarySkeleton } from './FacesheetSummarySkeleton';
import { FacesheetViewerHeader } from './FacesheetViewerHeader';
import { FacesheetViewerContent } from './content/FacesheetViewerContent';
import { ReferenceViewerContent } from './content/ReferenceViewerContent';
import { mapReferenceDetailToViewerContent } from './referenceDetailMapper';
import { useFacesheetReferenceScroll } from './useFacesheetReferenceScroll';
import type {
  CriteriaSection,
  FacesheetCriteriaRow,
  FacesheetViewerContext,
  FacesheetViewerDocument,
  ReferenceContentState,
  ViewerContentState,
} from './types';

type ReferenceDetail =
  inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetReferenceDetail'];
type HieContextRecords = NonNullable<ReferenceDetail['contextRecords']>;

const getReportScopeKey = (scope: ReferenceDetail['reportScope']) =>
  scope
    ? `${scope.sourceType ?? ''}\u0000${scope.sourceSystemRequestId}\u0000${scope.curatedTable}`
    : undefined;

interface FacesheetDocumentViewerProps {
  open: boolean;
  canDownload?: boolean;
  patientId?: string;
  document?: FacesheetViewerDocument;
  context?: FacesheetViewerContext;
  loadingTitle?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onClose: () => void;
}

export const FacesheetDocumentViewer = ({
  open,
  canDownload = false,
  patientId,
  document: facesheetDocument,
  context,
  loadingTitle,
  isLoading = false,
  isError = false,
  onRetry,
  onClose,
}: FacesheetDocumentViewerProps) => {
  const [contentState, setContentState] = useState<ViewerContentState>({
    contentType: 'facesheet',
  });
  const [hieContextByScope, setHieContextByScope] = useState<
    Record<string, HieContextRecords>
  >({});
  const [hieContextSourceByScope, setHieContextSourceByScope] = useState<
    Record<string, string>
  >({});
  const {
    targetPage,
    anchorScrollTarget,
    anchorScrollKey,
    clearScroll,
    handlePageChange,
    syncToReference,
    setTargetPage,
  } = useFacesheetReferenceScroll();

  const activeReferenceId =
    contentState.contentType === 'reference'
      ? contentState.reference.activeReferenceId
      : undefined;

  const activeReference =
    contentState.contentType === 'reference'
      ? contentState.reference.criteriaRow.references.find(
          (reference) => reference.id === activeReferenceId,
        )
      : undefined;
  const activeScopeKey = getReportScopeKey(activeReference?.reportScope);
  const cachedContextRecords = activeScopeKey
    ? hieContextByScope[activeScopeKey]
    : undefined;
  const contextSourceReferenceId = activeScopeKey
    ? hieContextSourceByScope[activeScopeKey]
    : undefined;
  const includeContext =
    cachedContextRecords === undefined ||
    contextSourceReferenceId === activeReferenceId;

  const referenceDetailQuery = useGetFacesheetReferenceDetail(
    patientId && facesheetDocument && activeReferenceId
      ? {
          patientId,
          facesheetRequestId: facesheetDocument.id,
          referenceId: activeReferenceId,
          includeContext,
        }
      : undefined,
  );
  const { data: patient } = useGetPatientById(patientId);

  const mappedReferenceDetail = useMemo(() => {
    const detail = referenceDetailQuery.data;
    if (!detail || detail.referenceId !== activeReferenceId) return undefined;
    const contextRecords =
      cachedContextRecords ??
      (detail.reportScope ? detail.contextRecords : undefined);
    return mapReferenceDetailToViewerContent(detail, contextRecords);
  }, [activeReferenceId, cachedContextRecords, referenceDetailQuery.data]);

  useEffect(() => {
    const detail = referenceDetailQuery.data;
    const scopeKey = getReportScopeKey(detail?.reportScope);
    if (
      !detail ||
      detail.referenceId !== activeReferenceId ||
      !scopeKey ||
      cachedContextRecords !== undefined
    ) {
      return;
    }

    const contextRecords = detail.contextRecords ?? [];
    if (contextRecords.length === 0) return;

    setHieContextByScope((current) => ({
      ...current,
      [scopeKey]: contextRecords,
    }));
    setHieContextSourceByScope((current) => ({
      ...current,
      [scopeKey]: detail.referenceId,
    }));
  }, [activeReferenceId, cachedContextRecords, referenceDetailQuery.data]);

  useEffect(() => {
    if (!facesheetDocument?.id) return;

    setContentState({ contentType: 'facesheet' });
    setHieContextByScope({});
    setHieContextSourceByScope({});
    clearScroll();
  }, [facesheetDocument?.id, clearScroll]);

  useEffect(() => {
    if (mappedReferenceDetail?.targetPage !== undefined) {
      setTargetPage(mappedReferenceDetail.targetPage);
    }
  }, [mappedReferenceDetail?.targetPage, setTargetPage]);

  const title = facesheetDocument?.title ?? loadingTitle ?? 'Facesheet';
  const isReferenceFromFacesheet =
    contentState.contentType === 'reference' &&
    contentState.reference.entrySource === 'facesheet';

  const handleHeaderBack = () => {
    if (isReferenceFromFacesheet) {
      setContentState({ contentType: 'facesheet' });
      clearScroll();
      return;
    }

    onClose();
  };

  const handleOpenReference = (
    row: FacesheetCriteriaRow,
    section: CriteriaSection,
    referenceId?: string,
  ) => {
    if (!row.references.length || !facesheetDocument) return;

    const nextReferenceId = referenceId ?? row.references[0]?.id;
    if (!nextReferenceId) return;

    const reference: ReferenceContentState = {
      criteriaRow: row,
      criteriaSection: section,
      activeReferenceId: nextReferenceId,
      entrySource: 'facesheet',
    };

    setContentState({
      contentType: 'reference',
      reference,
    });
    syncToReference(nextReferenceId, reference);
  };

  const handleSelectReference = (referenceId: string) => {
    if (contentState.contentType !== 'reference') return;

    const nextReference: ReferenceContentState = {
      ...contentState.reference,
      activeReferenceId: referenceId,
    };

    setContentState({
      contentType: 'reference',
      reference: nextReference,
    });
    syncToReference(referenceId, nextReference);
  };

  const referenceSource = mappedReferenceDetail?.source;
  const effectiveTargetPage = mappedReferenceDetail?.targetPage ?? targetPage;

  return (
    <Sheet open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <SheetContent
        fullScreen
        showCloseButton={false}
        className="bg-muted flex h-svh max-h-svh flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <FacesheetViewerHeader
          title={title}
          generatedAt={facesheetDocument?.generatedAt}
          patientFullName={context?.patientFullName}
          profileName={context?.profileName}
          profileAvatarType={context?.profileAvatarType}
          canDownload={canDownload}
          isReferenceView={contentState.contentType === 'reference'}
          allReferencesDownloadUrl={
            facesheetDocument?.allReferencesZipSignedUrl
          }
          currentReferenceDownload={
            referenceDetailQuery.data?.currentReferenceDownload ?? undefined
          }
          backAriaLabel={
            isReferenceFromFacesheet
              ? 'Back to facesheet'
              : 'Close document viewer'
          }
          onBack={handleHeaderBack}
        />

        {!facesheetDocument || isLoading ? (
          isError ? (
            <FacesheetLoadError onRetry={onRetry} />
          ) : (
            <FacesheetSummarySkeleton />
          )
        ) : contentState.contentType === 'facesheet' ? (
          <FacesheetViewerContent
            summary={facesheetDocument.summary}
            onOpenReference={handleOpenReference}
          />
        ) : referenceDetailQuery.isError ? (
          <FacesheetLoadError
            onRetry={() => void referenceDetailQuery.refetch()}
          />
        ) : (
          <ReferenceViewerContent
            reference={contentState.reference}
            source={referenceSource}
            patient={patient}
            targetPage={effectiveTargetPage}
            anchorScrollTarget={anchorScrollTarget}
            anchorScrollKey={anchorScrollKey}
            onPageChange={handlePageChange}
            onSelectReference={handleSelectReference}
            isDocumentLoading={
              !referenceSource || referenceDetailQuery.isFetching
            }
          />
        )}
      </SheetContent>
    </Sheet>
  );
};
